import { Building2, ChevronDown, ChevronRight, Plus, Edit3, Trash2, User, Cog } from 'lucide-react';
import { useState } from 'react';

import type { OrgTreeNode, UnitType } from '../hooks/useOrganizationalStructure';

import { UNIT_CONFIG } from './orgChart/konfiguracja';

// Diagram (widok domyślny profilu firmy) żyje w osobnym pliku — układ
// hybrydowy wiersz/kolumna, skala, przesuwanie. Re-eksport trzyma stary
// kontrakt importu dla `CompanyProfilePage.tsx`.
export { OrgChartDiagram } from './orgChart/OrgChartDiagram';

// ─── Props ────────────────────────────────────────────────────────────────
interface OrgChartProps {
  tree: OrgTreeNode[];
  organizationName?: string;
  editable?: boolean;
  onAddUnit?: (parentId: string | null, type: UnitType) => void;
  onEditUnit?: (unit: OrgTreeNode) => void;
  onDeleteUnit?: (unitId: string) => void;
  compact?: boolean;
}

// ─── Node Component ──────────────────────────────────────────────────────
interface NodeProps {
  node: OrgTreeNode;
  depth: number;
  editable: boolean;
  onAdd?: (parentId: string | null, type: UnitType) => void;
  onEdit?: (unit: OrgTreeNode) => void;
  onDelete?: (unitId: string) => void;
  isLast: boolean;
  compact: boolean;
}

function OrgNode({ node, depth, editable, onAdd, onEdit, onDelete, isLast, compact }: NodeProps) {
  const [expanded, setExpanded] = useState(depth < 2);
  const [showActions, setShowActions] = useState(false);
  const config = UNIT_CONFIG[node.unit_type];
  const Icon = config.icon;
  const hasChildren = node.children.length > 0;
  const nodeColor = config.defaultColor;

  return (
    <div className="relative">
      {/* Connector line from parent */}
      {depth > 0 && (
        <div
          className="absolute left-0 top-0 w-6 border-l-2 border-b-2 border-aurora-border-strong rounded-bl-ids-md"
          style={{ height: '24px', marginLeft: '-1px' }}
        />
      )}
      {/* Vertical connector for siblings */}
      {depth > 0 && !isLast && (
        <div
          className="absolute left-0 top-0 bottom-0 border-l-2 border-aurora-border-strong"
          style={{ marginLeft: '-1px' }}
        />
      )}

      {/* Node Card.
          Indentation is an inline style, not `ml-${...}`: Tailwind only ships classes
          it can find as complete literals in the source, so the interpolated form was
          purged at build time and produced no margin at all. */}
      <div
        className="group relative mb-2"
        style={{ marginLeft: depth > 0 ? '1.5rem' : 0 }}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        <div
          className={`flex items-center gap-3 ${compact ? 'px-3 py-2' : 'px-4 py-3'} rounded-ids-lg border transition-all duration-200 cursor-pointer
            ${config.bgClass} ${config.borderClass}
            hover:bg-aurora-bg-subtle hover:border-aurora-border-strong`}
          style={{
            borderLeftWidth: '3px',
            borderLeftColor: nodeColor,
          }}
          onClick={() => hasChildren && setExpanded(!expanded)}
        >
          {/* Expand/collapse toggle */}
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            {hasChildren ? (
              expanded ? (
                <ChevronDown className="w-4 h-4 text-aurora-muted" />
              ) : (
                <ChevronRight className="w-4 h-4 text-aurora-muted" />
              )
            ) : (
              <div className="w-1.5 h-1.5 rounded-full bg-gray-500" />
            )}
          </div>

          {/* Icon */}
          <div
            className={`w-8 h-8 rounded-ids-md flex items-center justify-center flex-shrink-0`}
            style={{ backgroundColor: `${nodeColor}20` }}
          >
            <Icon className="w-4 h-4" style={{ color: nodeColor }} />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-aurora-text truncate">{node.name}</span>
              {node.code && <span className="badge mono">{node.code}</span>}
            </div>
            {!compact && node.description && (
              <p className="text-xs text-aurora-muted truncate mt-0.5">{node.description}</p>
            )}
          </div>

          {/* Counters */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {(node.employee_count ?? 0) > 0 && (
              <span
                className="flex items-center gap-1 text-xs text-aurora-muted mono"
                title="Pracownicy"
              >
                <User className="w-3 h-3" />
                {node.employee_count}
              </span>
            )}
            {(node.equipment_count ?? 0) > 0 && (
              <span
                className="flex items-center gap-1 text-xs text-aurora-muted mono"
                title="Sprzęt"
              >
                <Cog className="w-3 h-3" />
                {node.equipment_count}
              </span>
            )}
            {hasChildren && (
              <span className="text-xs text-aurora-subtle mono">{node.children.length}</span>
            )}
          </div>

          {/* Type Badge */}
          <span
            className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${config.bgClass} ${config.textClass}`}
          >
            {config.labelPl}
          </span>

          {/* Action Buttons (edit mode) */}
          {editable && showActions && (
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAdd?.(node.id, 'department');
                }}
                className="p-1 rounded-ids-sm hover:bg-aurora-bg-subtle text-aurora-muted hover:text-aurora-brand transition-colors"
                title="Dodaj podelement"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.(node);
                }}
                className="p-1 rounded-ids-sm hover:bg-aurora-bg-subtle text-aurora-muted hover:text-amber-400 transition-colors"
                title="Edytuj"
              >
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(node.id);
                }}
                className="p-1 rounded-ids-sm hover:bg-aurora-bg-subtle text-aurora-muted hover:text-red-400 transition-colors"
                title="Usuń"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Children */}
        {hasChildren && expanded && (
          <div className="ml-8 mt-1 relative">
            {node.children.map((child, idx) => (
              <OrgNode
                key={child.id}
                node={child}
                depth={depth + 1}
                editable={editable}
                onAdd={onAdd}
                onEdit={onEdit}
                onDelete={onDelete}
                isLast={idx === node.children.length - 1}
                compact={compact}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main OrgChart Component ─────────────────────────────────────────────
export function OrgChart({
  tree,
  editable = false,
  onAddUnit,
  onEditUnit,
  onDeleteUnit,
  compact = false,
}: OrgChartProps) {
  if (tree.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 rounded-ids-xl bg-aurora-surface border border-aurora-border flex items-center justify-center mb-4">
          <Building2 className="w-8 h-8 text-aurora-subtle" />
        </div>
        <p className="text-aurora-muted text-sm">Brak zdefiniowanej struktury organizacyjnej</p>
        {editable && onAddUnit && (
          <button onClick={() => onAddUnit(null, 'department')} className="mt-4 btn btn--tonal">
            <Plus className="w-4 h-4" />
            Dodaj pierwszy dział
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* Root Add Button */}
      {editable && onAddUnit && (
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => onAddUnit(null, 'department')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-ids-md bg-blue-500/10 border border-blue-500/20 text-blue-400 hover:bg-blue-500/20 transition-colors text-xs font-semibold"
          >
            <Plus className="w-3.5 h-3.5" />
            Dział
          </button>
          <button
            onClick={() => onAddUnit(null, 'position')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-ids-md bg-purple-500/10 border border-purple-500/20 text-purple-400 hover:bg-purple-500/20 transition-colors text-xs font-semibold"
          >
            <Plus className="w-3.5 h-3.5" />
            Stanowisko
          </button>
          <button
            onClick={() => onAddUnit(null, 'team')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-ids-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-colors text-xs font-semibold"
          >
            <Plus className="w-3.5 h-3.5" />
            Zespół
          </button>
        </div>
      )}

      {/* Tree View */}
      {tree.map((node, idx) => (
        <OrgNode
          key={node.id}
          node={node}
          depth={0}
          editable={editable}
          onAdd={onAddUnit}
          onEdit={onEditUnit}
          onDelete={onDeleteUnit}
          isLast={idx === tree.length - 1}
          compact={compact}
        />
      ))}
    </div>
  );
}
